import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { leadDetailQueryOptions } from 'app/features/leads/queries/lead-detail-query-options';
import { updateLeadStatusMutationOptions } from 'app/features/leads/queries/update-lead-status-mutation-options';
import { LeadsQueryProvider } from 'app/features/leads/providers/leads-query-provider';
import type { LeadStatus } from 'app/features/leads/services/gym-finder/types';
import { routes } from 'app/routes/constants';
import Button from 'app/ui/components/atoms/button';
import Typography from 'app/ui/components/atoms/typography';
import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import FormFieldSet from 'app/ui/components/molecules/form-field-set';
import Loading from 'app/ui/components/molecules/loading';

const STATUS_OPTIONS: LeadStatus[] = ['active', 'paused', 'trial'];

const PageContent = styled.div`
  padding: ${({ theme: { spacing } }) => `${spacing(4)} 0 ${spacing(12)}`};
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(8)};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.xsmall};
`;

const BreadcrumbLink = styled(Link)`
  color: ${({ theme: { colors } }) => colors.knop};
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${({ theme: { colors } }) => colors.focoltone};
  }
`;

const BreadcrumbCurrent = styled.span`
  color: ${({ theme: { colors } }) => colors.focoltone};
`;

const ProfileHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(10)};
`;

const Avatar = styled.div`
  width: ${({ theme: { spacing } }) => spacing(25)};
  height: ${({ theme: { spacing } }) => spacing(25)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme: { colors } }) => colors.charcoal};
  color: ${({ theme: { colors } }) => colors.focoltone};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.jumbo};
  font-weight: ${({ theme: { typography } }) => typography.bold};
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const ContactText = styled(Typography)`
  color: ${({ theme: { colors } }) => colors.lightGray};
`;

const ContactDivider = styled.span`
  width: 0.0625rem;
  height: ${({ theme: { spacing } }) => spacing(5)};
  background: ${({ theme: { colors } }) => colors.blackSwan};
`;

const DetailGrid = styled.div`
  max-width: ${({ theme: { spacing } }) => spacing(161)};
`;

const SectionTitle = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => spacing(5)};
`;

const DetailRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(5)};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  border-bottom: 0.0625rem solid ${({ theme: { colors } }) => colors.veryGray};
  padding: ${({ theme: { spacing } }) => `0 ${spacing(1)} ${spacing(2.5)}`};
`;

const DetailValue = styled(Typography)`
  font-weight: ${({ theme: { typography } }) => typography.bold};
  text-align: right;
`;

const NoteBlock = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => spacing(5)};
`;

const NoteLabel = styled(Typography)`
  display: block;
  margin-bottom: ${({ theme: { spacing } }) => spacing(2)};
`;

const StatusField = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => spacing(3.5)};

  fieldset {
    margin: 0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme: { spacing } }) => spacing(2.5)};
`;

const ErrorText = styled(Typography)`
  color: ${({ theme: { colors } }) => colors.firebreathingRed};
  margin-bottom: ${({ theme: { spacing } }) => spacing(3)};
`;

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const LeadDetailPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { leadId } = useParams<{ leadId: string }>();
  const [selectedStatus, setSelectedStatus] = useState<LeadStatus>('active');

  const { data: lead, isPending, isError } = useQuery(leadDetailQueryOptions(leadId ?? ''));
  const updateLeadStatus = useMutation({
    ...updateLeadStatusMutationOptions(),
    onSuccess: async (updatedLead) => {
      queryClient.setQueryData(['leads', 'detail', updatedLead.id], updatedLead);
      await queryClient.invalidateQueries({ queryKey: ['leads', 'list'] });
    },
  });

  useEffect(() => {
    if (lead) {
      setSelectedStatus(lead.status);
    }
  }, [lead]);

  if (!leadId) {
    return <ErrorComponent />;
  }

  if (isPending) {
    return <Loading />;
  }

  if (isError || !lead) {
    return <ErrorComponent />;
  }

  const pageTitle = lead.athleteName;
  const hasStatusChanged = selectedStatus !== lead.status;
  const formatDate = (value: string) => intl.formatDate(new Date(value), DATE_FORMAT_OPTIONS);
  const statusOptions = STATUS_OPTIONS.map((status) => ({
    id: status,
    name: intl.formatMessage({ id: `leads.status.${status}` }),
  }));
  const selectedStatusLabel = intl.formatMessage({ id: `leads.status.${selectedStatus}` });

  const handleCancel = () => {
    navigate(routes.LEADS);
  };

  const handleSave = () => {
    updateLeadStatus.mutate({ id: lead.id, status: selectedStatus });
  };

  return (
    <GenericPage pageTitle={pageTitle}>
      <PageContent>
        <Breadcrumb aria-label={intl.formatMessage({ id: 'leads.detail.breadcrumbLabel' })}>
          <BreadcrumbLink to={routes.LEADS}>{intl.formatMessage({ id: 'leads.pageTitle' })}</BreadcrumbLink>
          <span>{'>'}</span>
          <BreadcrumbCurrent>{lead.athleteName}</BreadcrumbCurrent>
        </Breadcrumb>

        <ProfileHeader>
          <Avatar aria-hidden={true}>{getInitials(lead.athleteName)}</Avatar>
          <div>
            <Typography variant="h2">{lead.athleteName}</Typography>
            <ContactRow>
              <ContactText variant="body2">{lead.athleteEmail}</ContactText>
              <ContactDivider />
              <ContactText variant="body2">{lead.athletePhone}</ContactText>
            </ContactRow>
          </div>
        </ProfileHeader>

        <DetailGrid>
          <SectionTitle variant="h4">{intl.formatMessage({ id: 'leads.detail.title' })}</SectionTitle>
          <DetailRows>
            <DetailRow>
              <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.signedUpForHyrox' })}</Typography>
              <DetailValue variant="body2">
                {intl.formatMessage({ id: lead.signedUpForHyrox ? 'leads.detail.yes' : 'leads.detail.no' })}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.interestedIn' })}</Typography>
              <DetailValue variant="body2">{lead.interestedIn}</DetailValue>
            </DetailRow>
            <DetailRow>
              <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.appliedAt' })}</Typography>
              <DetailValue variant="body2">{formatDate(lead.receivedAt)}</DetailValue>
            </DetailRow>
            <DetailRow>
              <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.lastUpdated' })}</Typography>
              <DetailValue variant="body2">{formatDate(lead.updatedAt)}</DetailValue>
            </DetailRow>
          </DetailRows>

          <NoteBlock>
            <NoteLabel variant="label">{intl.formatMessage({ id: 'leads.detail.note' })}</NoteLabel>
            <Typography variant="caption">{lead.message}</Typography>
          </NoteBlock>

          <StatusField>
            <FormFieldSet
              label={intl.formatMessage({ id: 'leads.detail.status' })}
              type="dropdown"
              options={statusOptions}
              noValueText={intl.formatMessage({ id: 'leads.detail.status' })}
              value={selectedStatusLabel}
              onChange={({ id }) => setSelectedStatus(id as LeadStatus)}
              disabled={updateLeadStatus.isPending}
              variant="dark"
            />
          </StatusField>

          {updateLeadStatus.isError && (
            <ErrorText variant="caption">{intl.formatMessage({ id: 'leads.detail.saveError' })}</ErrorText>
          )}

          <ButtonRow>
            <Button
              label={intl.formatMessage({ id: 'leads.detail.cancel' })}
              type="secondary-white"
              onClick={handleCancel}
            />
            <Button
              label={intl.formatMessage({ id: 'leads.detail.saveChanges' })}
              type="primary"
              state={updateLeadStatus.isPending || !hasStatusChanged ? 'disabled' : 'default'}
              onClick={handleSave}
            />
          </ButtonRow>
        </DetailGrid>
      </PageContent>
    </GenericPage>
  );
};

const LeadDetailPage = () => (
  <LeadsQueryProvider>
    <LeadDetailPageContent />
  </LeadsQueryProvider>
);

export default LeadDetailPage;
